import { ReactNode, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { Card } from "../../UI/Card.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../../UI/Layout/BreadCrumbsItem.tsx";
import { SegmentedControl } from "../../UI/SegmentedControl.tsx";
import { SummaryStat } from "../../UI/Chart/SummaryStat.tsx";
import { LoadingBlock } from "../../UI/Loading/LoadingRows.tsx";
import { OnboardingLivesTab } from "../OnboardingLivePaths.ts";
import { onboardingLiveTimezone } from "../onboardingLiveTime.ts";
import { useOnboardingLiveConductors } from "../useOnboardingLiveConductors.ts";
import { useBackToOnboardingLivesList } from "../List/onboardingLivesListNavigation.ts";
import {
  DEFAULT_STATISTICS_PERIOD,
  OVERALL_STATISTICS_PERIOD,
  STATISTICS_PERIOD_CHOICES,
  StatisticsPeriod,
  StatisticsPeriodChoice,
} from "./onboardingLiveStatisticsPeriod.ts";
import { Peak } from "./onboardingLiveStatistics.ts";
import { useOnboardingLiveStatistics } from "./useOnboardingLiveStatistics.ts";
import {
  formatDay,
  formatDecimal,
  formatFrequency,
  formatShare,
} from "./statisticsFormat.ts";
import { OnboardingLiveWeeklyTrendChart } from "./OnboardingLiveWeeklyTrendChart.tsx";
import { OnboardingLiveEnrollmentHeatmap } from "./OnboardingLiveEnrollmentHeatmap.tsx";
import { OnboardingLiveConductorsTable } from "./OnboardingLiveConductorsTable.tsx";

const PERIOD_OPTIONS = STATISTICS_PERIOD_CHOICES.map((choice) => ({
  value: choice,
  label:
    choice === OVERALL_STATISTICS_PERIOD ? "Overall" : `${choice} settimane`,
}));

function describePeriodChoice(choice: StatisticsPeriodChoice): string {
  return choice === OVERALL_STATISTICS_PERIOD
    ? "dalla prima live"
    : `ultime ${choice} settimane, inclusa quella in corso`;
}

function StatDetail({ children }: { children: ReactNode }) {
  return <span className={"text-xs text-slate-400"}>{children}</span>;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className={"mt-4"}>
      <Card>
        <div className={"flex flex-col gap-1"}>
          <h2
            className={
              "text-sm font-semibold text-slate-500 uppercase tracking-wide"
            }
          >
            {title}
          </h2>
          {description ? (
            <span className={"text-xs text-slate-400"}>{description}</span>
          ) : null}
        </div>
        {children}
      </Card>
    </div>
  );
}

function describePeak(peak: Peak | undefined): string {
  return peak ? `Picco ${peak.value} · ${formatDay(peak.startUtc)}` : "—";
}

function describePeriod(period: StatisticsPeriod): string {
  const from = period.from.tz(onboardingLiveTimezone).locale("it");
  const to = period.to.tz(onboardingLiveTimezone).locale("it");
  return `${from.format("D MMM")} – ${to.format("D MMM YYYY")}`;
}

export function OnboardingLiveStatisticsScreen() {
  const backToList = useBackToOnboardingLivesList(OnboardingLivesTab.Lives);
  const now = useMemo(() => dayjs(), []);
  const [periodChoice, setPeriodChoice] = useState<StatisticsPeriodChoice>(
    DEFAULT_STATISTICS_PERIOD,
  );
  const {
    period,
    statistics,
    loadingStatistics,
    funnel,
    upcomingLives,
    loadingUpcomingLives,
  } = useOnboardingLiveStatistics(periodChoice, now);
  const { teacherNameOf } = useOnboardingLiveConductors();
  const upcomingEnrolled = upcomingLives.reduce(
    (sum, live) => sum + live.participantsCount,
    0,
  );

  return (
    <Layout>
      <Card>
        <div
          className={
            "flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          }
        >
          <ScreenTitleBlock
            title={"Statistiche live onboarding"}
            breadCrumbs={
              <BreadCrumbsWrapper>
                <BreadCrumbsItem
                  onClick={backToList}
                  title={"Live onboarding"}
                />
                <BreadCrumbsItem title={"Statistiche"} />
              </BreadCrumbsWrapper>
            }
          />
          <SegmentedControl
            options={PERIOD_OPTIONS}
            value={periodChoice}
            onChange={setPeriodChoice}
          />
        </div>
        <span className={"text-xs text-slate-400"}>
          {describePeriod(period)} · {describePeriodChoice(periodChoice)}
        </span>
      </Card>

      <Section title={"Live"}>
        <div className={"grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6"}>
          <SummaryStat
            label={"Live svolte"}
            loading={loadingStatistics}
            value={String(statistics?.doneLives ?? 0)}
          >
            {statistics && (
              <StatDetail>
                {formatFrequency(statistics.livesPerDay)} ·{" "}
                {statistics.daysWithALive} giorni su {statistics.daysInPeriod}{" "}
                con almeno una live
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Iscritti medi per live"}
            loading={loadingStatistics}
            value={formatDecimal(statistics?.averageEnrolled ?? null)}
          >
            {statistics && (
              <StatDetail>{describePeak(statistics.enrolledPeak)}</StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Partecipanti medi per live"}
            loading={loadingStatistics}
            value={formatDecimal(statistics?.averageAttended ?? null)}
          >
            {statistics && (
              <StatDetail>
                {describePeak(statistics.attendedPeak)} · dai feedback dei
                conduttori
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Tasso di presenza"}
            loading={loadingStatistics}
            value={formatShare(statistics?.attendanceRate ?? null)}
          >
            {statistics && (
              <StatDetail>
                Partecipanti su iscritti, nelle live con feedback
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Feedback inviati dai conduttori"}
            loading={loadingStatistics}
            value={formatShare(statistics?.feedbackShare ?? null)}
          >
            {statistics && (
              <StatDetail>
                {statistics.livesWithFeedback} su {statistics.doneLives} live
                svolte
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Live in programma"}
            loading={loadingUpcomingLives}
            value={String(upcomingLives.length)}
          >
            {!loadingUpcomingLives && (
              <StatDetail>{upcomingEnrolled} studenti già iscritti</StatDetail>
            )}
          </SummaryStat>
        </div>
      </Section>

      <Section
        title={"Andamento settimanale"}
        description={"Iscritti e partecipanti delle live svolte ogni settimana"}
      >
        {statistics ? (
          <OnboardingLiveWeeklyTrendChart weeks={statistics.weeks} />
        ) : (
          <LoadingBlock sizeClassName={"h-56 w-full"} />
        )}
      </Section>

      <Section
        title={"Giorni e orari più richiesti"}
        description={
          "Iscritti medi per live, per giorno e ora d'inizio: non dipende da quante live si fanno in quello slot"
        }
      >
        {statistics ? (
          <OnboardingLiveEnrollmentHeatmap
            cells={statistics.enrollmentByWeekdayAndHour}
          />
        ) : (
          <LoadingBlock sizeClassName={"h-40 w-full"} />
        )}
      </Section>

      <Section
        title={"Conduttori"}
        description={"Ordinati per numero di live condotte nel periodo"}
      >
        {statistics ? (
          <OnboardingLiveConductorsTable
            conductors={statistics.conductors}
            teacherNameOf={teacherNameOf}
          />
        ) : (
          <LoadingBlock sizeClassName={"h-40 w-full"} />
        )}
      </Section>

      <Section
        title={"Disdette e cambi"}
        description={
          "Disdette e cambi di conduttore vengono registrati da questa versione in poi: prima risultano a zero"
        }
      >
        <div className={"grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6"}>
          <SummaryStat
            label={"Disdette degli studenti"}
            loading={loadingStatistics}
            value={String(statistics?.cancellationsByStudents ?? 0)}
          >
            <StatDetail>
              Studenti che hanno annullato la prenotazione
            </StatDetail>
          </SummaryStat>
          <SummaryStat
            label={"Rimossi dallo staff"}
            loading={loadingStatistics}
            value={String(statistics?.cancellationsByStaff ?? 0)}
          >
            <StatDetail>Iscritti tolti dal dettaglio della live</StatDetail>
          </SummaryStat>
          <SummaryStat
            label={"Live eliminate dallo staff"}
            loading={loadingStatistics}
            value={String(statistics?.deletedLives ?? 0)}
          >
            {statistics && (
              <StatDetail>
                {statistics.studentsOfDeletedLives} studenti iscritti coinvolti
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Cambi di conduttore"}
            loading={loadingStatistics}
            value={String(statistics?.conductorChanges ?? 0)}
          >
            <StatDetail>Per conduttore nella tabella sopra</StatDetail>
          </SummaryStat>
        </div>
      </Section>

      <Section
        title={"Studenti"}
        description={
          "Solo gli studenti che devono fare la live: chi ha completato l'onboarding a mano dal backoffice non è contato nei giorni"
        }
      >
        <div className={"grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6"}>
          <SummaryStat
            label={"Studenti con la live da fare"}
            loading={!funnel}
            value={String(funnel?.usersWithOnboardingLiveToBeDone ?? 0)}
          >
            {funnel && (
              <StatDetail>
                {funnel.usersWaitingForMoreThanAWeek} iscritti alla piattaforma
                da più di 7 giorni
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Giorni per completare l'onboarding"}
            loading={!funnel}
            value={formatDecimal(
              funnel?.medianDaysToCompleteOnboardingLive ?? null,
            )}
          >
            {funnel && (
              <StatDetail>
                Mediana dall'iscrizione alla live svolta · media{" "}
                {formatDecimal(
                  funnel.averageDaysToCompleteOnboardingLive ?? null,
                )}
              </StatDetail>
            )}
          </SummaryStat>
          <SummaryStat
            label={"Onboarding completati"}
            loading={!funnel}
            value={String(funnel?.usersWhoCompletedOnboardingLive ?? 0)}
          >
            <StatDetail>
              Studenti che hanno svolto la live nel periodo
            </StatDetail>
          </SummaryStat>
        </div>
      </Section>
    </Layout>
  );
}
